import { VisitorInside } from '../../../interfaces/hierarchy-interfaces';
import { arrivedText } from '../../../function-utils/building-time';

/**
 * MATCHING A SCANNED NAME TO AN OPEN ARRIVAL — in the browser, on purpose.
 * ---------------------------------------------------------------------------
 *
 * Visitor names are AES-GCM with a random IV (migration 040), so no SQL
 * predicate can match one and the server cannot be asked "who here is called
 * Maria". The presence endpoint returns the building's open arrivals with the
 * names already decrypted; matching therefore happens app-side, against that
 * list, and the result is a ROW ID that POST /visitor-log/:id/exit can close.
 *
 * ============ IT SELECTS. IT NEVER SIGNS ANYBODY OUT ============
 *
 * A departure is a chain-of-custody record and the model's reading of a
 * passport is a suggestion. Every function here narrows a list; none of them
 * confirms anything. The desk presses the button.
 */

/**
 * Fold to something two humans typing the same name would agree on: strip
 * accents, case and punctuation. 'Ana-María O'Brien' and 'ana maria obrien'
 * become the same string, because a passport, a keyboard and a courier's
 * handwriting disagree about all three.
 */
export const normaliseName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const nameTokens = (value: string): string[] =>
  normaliseName(value)
    .split(' ')
    .filter(token => token.length > 0);

/** How many adjacent tokens may be glued together — enough for "van der berg". */
const MAX_JOIN = 3;

/**
 * THE APOSTROPHE PROBLEM, and why a token list is not enough.
 *
 * A passport's machine-readable zone spells it OBRIEN. The concierge typed
 * O'Brien. Normalising punctuation to a space turns the second into two tokens
 * and the first into one, and a plain token comparison then decides they are
 * different people — which on this screen means the desk scans the ID of
 * somebody standing in front of them and is told nobody by that name is inside.
 * Hyphenated surnames, "Mac Donald" and "de la Cruz" all fail the same way.
 *
 * So a value's ATOMS are its tokens plus every run of up to three adjacent
 * tokens glued together: 'o brien' yields 'obrien', 'van der berg' yields
 * 'vanderberg'. The query is then matched against the atoms.
 *
 * Atoms are built PER SOURCE STRING (each person, the unit, the type) rather
 * than over one concatenation, so gluing can never manufacture a match that
 * spans two different facts about the visitor.
 */
export const nameAtoms = (values: (string | null | undefined)[]): string[] => {
  const atoms = new Set<string>();

  values.forEach(value => {
    const tokens = nameTokens(value ?? '');
    tokens.forEach((token, i) => {
      atoms.add(token);
      let joined = token;
      for (let n = 1; n < MAX_JOIN && i + n < tokens.length; n += 1) {
        joined += tokens[i + n];
        atoms.add(joined);
      }
    });
  });

  return [...atoms];
};

/**
 * Every token of the query must PREFIX some atom of the row. Prefixes rather
 * than equality because a desk types "mar" and expects Maria, and because a
 * scanner that reads "MARIA J" must still find "Maria Jimenez". Order does not
 * matter: "obrien ana" finds "Ana O'Brien".
 *
 * The unit label and the visitor type are part of the haystack — "4b" and
 * "vendor" are how a concierge finds the contractor whose name nobody wrote
 * down.
 *
 * An empty query returns the list untouched: no query is not a filter.
 */
export const filterArrivals = (
  rows: VisitorInside[],
  query: string
): VisitorInside[] => {
  const tokens = nameTokens(query);
  if (tokens.length === 0) return rows;

  return rows.filter(row => {
    const atoms = nameAtoms([...row.people, row.unit_label, row.visitor_type]);

    return tokens.every(token => atoms.some(atom => atom.startsWith(token)));
  });
};

/**
 * What a scan found. Three outcomes and they are shown differently, because
 * "nobody by that name is signed in" and "two people are" are different
 * problems for the person holding the passport.
 */
export type ScanOutcome =
  | { kind: 'none'; scanned: string }
  | { kind: 'one'; scanned: string; row: VisitorInside }
  | { kind: 'many'; scanned: string; rows: VisitorInside[] };

/**
 * Match a scanned document against the open arrivals.
 *
 * Note what this does NOT return: a decision. Even the single-match case hands
 * back the row for the desk to confirm — a model reading a worn driving licence
 * must not be able to close somebody's arrival on its own.
 */
export const matchScannedName = (
  rows: VisitorInside[],
  firstName: string,
  lastName: string
): ScanOutcome => {
  const scanned = `${firstName} ${lastName}`.trim();
  const matches = filterArrivals(rows, scanned);

  if (nameTokens(scanned).length === 0 || matches.length === 0) {
    return { kind: 'none', scanned };
  }
  if (matches.length === 1) {
    return { kind: 'one', scanned, row: matches[0] };
  }

  return { kind: 'many', scanned, rows: matches };
};

/**
 * How a row is named on screen. An arrival with no name is normal — a delivery
 * driver, a contractor — and it must read as a record with no name rather than
 * as a truncated one, or the desk goes looking for the missing half.
 */
export const describeArrival = (row: VisitorInside): string => {
  const named = row.people.map(p => p.trim()).filter(Boolean);
  if (named.length === 0) {
    return row.visitor_type ? `${row.visitor_type} (no name recorded)` : '(no name recorded)';
  }
  if (named.length === 1) return named[0];

  return `${named[0]} + ${named.length - 1} more`;
};

/**
 * The second line: where they went and when, on the BUILDING's clock.
 *
 * Rendered from the INSTANT in the zone it is handed — the building's, on this
 * desk screen (R-T12): the server's own `arrived_local` is on the clock it
 * answered the reader on, which for an owner at the desk is not the building's.
 * It is never read through a Date on the device's clock; with no zone handed
 * over, the server's text is used as it is.
 */
export const describeArrivalMeta = (row: VisitorInside, zone?: string | null): string => {
  const arrived = zone ? arrivedText(row, zone) : row.arrived_local;

  return [
    row.unit_label && `Unit ${row.unit_label}`,
    row.visitor_type,
    row.headcount && row.headcount > 1 ? `${row.headcount} people` : null,
    row.stale ? `Arrived ${arrived}` : `Arrived ${arrived.slice(-5)}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The ghost tag. A row from a previous local day that nobody closed: it is not
 * evidence anybody is still in the building, and it must never look like
 * somebody who walked in on this shift.
 */
export { describeStale } from '../../presence/presence-format';
